import React, { useCallback, useMemo, useState } from "react";
import { DialogContent, Grid, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import style from "./index.module.css";

import Modal from "@/components/Modal";
import GetImages from "@/components/GetImages";
import Button from "@/components/Button";
import Confirmation from "@/components/Confirmation";
import CreatePrescription from "@/views/Doctor/Prescription/CreatePrescription";
import Dialog from "@/components/Dialog";
import MedicalRecords from "@/views/Common/MedicalRecords";

import { IButtonVariant } from "@/models/button";
import {
  IAppointmetEnums,
  IDoctorAvailabilityPlatform,
  IDoctorScheduleConsultationType,
  IPatientAppointmentStatus,
} from "@/models/doctor";
import {
  IAppointmentPermissionEnums,
  IModalKeyEnums,
  IPrescriptionPermissionEnums,
  IMedicalRecordPermissionEnums,
} from "@/models/permission";
import { IRoleType } from "@/models/role";
import { ISessionKeys } from "@/models";

import { timeInAmPmFormat } from "@/utils";
import useCancelScheduleAppointments from "@/utils/hooks/useCancelScheduleAppointments";
import { storage } from "@/utils/Storage";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";
import { useVideoCallStartFromDoctorJRDoctor } from "@/utils/hooks/useInitiateVideoCall";

import {
  prescriptionDetails,
  setPrescriptionTemplateValues,
  setShowCreatePrescriptionDialog,
} from "@/store/slices/doctor/prescription";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { appointmentDetails } from "@/store/slices/doctor/appointment";
import { setChatDialogOpen } from "@/store/slices/chat";

import { useFirebaseMessagingContext } from "@/context/Chat";

interface IProps {
  isOpen: boolean;
  platform: IDoctorAvailabilityPlatform;
  status: IPatientAppointmentStatus;
  closeHandler: () => void;
  openAppointmentSlotHandler: (type: IAppointmetEnums) => void;
}

const ScheduleEventPopUps: React.FC<IProps> = (props) => {
  const { platform, status, isOpen, closeHandler, openAppointmentSlotHandler } =
    props;
  const [showMedicalRecords, setShowMedicalRecords] = useState(false);
  const [showCancelSchedule, setShowCancelSchedule] = useState<boolean>(false);

  const sessionKeys = [
    ISessionKeys.USER_ID,
    ISessionKeys.ROLE_TYPE,
    ISessionKeys.DOCTOR_ID,
  ];
  const [currentUserId, roleType, doctorId] = sessionKeys?.map((key) =>
    storage.getSessionToken(key)
  );

  const doctorOrJrDoctorRole =
    roleType === IRoleType.DOCTOR || roleType === IRoleType.JR_DOCTOR;

  const dispatch = useAppDispatch();
  const { appointmentDetail } = useAppSelector(appointmentDetails);
  const { showCreatePrescriptionDialog } = useAppSelector(prescriptionDetails);

  const { cancelScheduleAppointments } = useCancelScheduleAppointments();
  const { initiateVideoCallHandler, initiateVideoCallLoading } =
    useVideoCallStartFromDoctorJRDoctor();
  const { handleChannel } = useFirebaseMessagingContext();

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isMobileOrTablet = isMobile || isTablet;

  const permissionRestrictionHandler = usePermissionRestriction();
  const navigate = useNavigate();

  const openCloseAppointmentCancelConfirmation = () => {
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.APPOINTMENT,
      IAppointmentPermissionEnums.CANCEL_APPOINTMENT
    );
    if (!isPermissionGranted) return;
    setShowCancelSchedule(!showCancelSchedule);
  };
  const handleShow = () => {
    setShowMedicalRecords((state) => !state);
  };

  const showMedicalRecordsHandler = () => {
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.MEDICAL_RECORD,
      IMedicalRecordPermissionEnums.VIEW_MEDICAL_RECORDS
    );
    if (!isPermissionGranted) return;
    handleShow();
  };

  const createPrescriptionHandler = () => {
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.PRESCRIPTION,
      IPrescriptionPermissionEnums.CREATE_PRESCRIPTION
    );
    if (!isPermissionGranted) return;
    dispatch(setShowCreatePrescriptionDialog(true));
  };

  const createPrescriptionBtn = useCallback(() => {
    dispatch(setPrescriptionTemplateValues({}));
    return (
      <Button
        btnTrigger={createPrescriptionHandler}
        variant={IButtonVariant.WHITE}
      >
        <GetImages name="PrescriptionIcon" width="20" height="20" />
        Create Prescription
      </Button>
    );
  }, [platform]);

  const rescheduleAppointmentHandler = () => {
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.APPOINTMENT,
      IAppointmentPermissionEnums.RESCHEDULE_APPOINTMENT
    );
    if (!isPermissionGranted) return;
    openAppointmentSlotHandler(IAppointmetEnums.CREATION);
  };

  const messageHandler = () => {
    const channelId = `app_${
      roleType === IRoleType.DOCTOR ? currentUserId : doctorId
    }_${appointmentDetail?.patient?.memberId}`;
    handleChannel(channelId);
    navigate("/chats");
    dispatch(setChatDialogOpen(true));
  };

  const content = useMemo(() => {
    return (
      <>
        <Grid item className={style.schedulePopUpContainer}>
          <Typography component={"h4"}>
            {platform === IDoctorAvailabilityPlatform.VIDEO_CALL
              ? timeInAmPmFormat(appointmentDetail?.slotStartTime)
              : `Slot - ${appointmentDetail?.slotNo}`}{" "}
            {platform === IDoctorAvailabilityPlatform.VIDEO_CALL && (
              <Typography component={"span"}>
                {appointmentDetail?.slotDuration?.toFixed(2)} min session
              </Typography>
            )}
          </Typography>
          <Typography component={"p"} className={style.patientName}>
            {appointmentDetail?.patient?.name}
          </Typography>
          <Typography component={"p"} className={style.patientAge}>
            Age - {appointmentDetail?.patient?.age}
          </Typography>
          {doctorOrJrDoctorRole && (
            <Grid item className={style.medicalRecordContainer}>
              <Grid item className={style.medicalRecordCounter}>
                <GetImages name="MedicalRecordIcon" height="24" width="24" />
                <Typography component={"p"}>
                  Medical Records ({appointmentDetail?.patient?.records})
                </Typography>
              </Grid>
              {appointmentDetail?.patient?.records >= 0 && (
                <Grid item>
                  <Typography
                    component={"p"}
                    className={style.medicalRecordShow}
                    onClick={showMedicalRecordsHandler}
                  >
                    Show
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
          <Grid item className={style.actionsBtnContainer}>
            {status === IPatientAppointmentStatus.CANCELLED && (
              <Button
                variant={IButtonVariant.WHITE}
                btnTrigger={rescheduleAppointmentHandler}
              >
                Reschedule
              </Button>
            )}
            {status === IPatientAppointmentStatus.COMPLETED &&
            (platform === IDoctorAvailabilityPlatform.IN_CLINIC ||
              platform === IDoctorAvailabilityPlatform.VIDEO_CALL) ? (
              <>
                <Button
                  variant={IButtonVariant.WHITE}
                  btnTrigger={messageHandler}
                >
                  Message
                </Button>
                <Button>
                  <GetImages name="CallWhiteIcon" width="25" height="20" /> Call
                  Patient
                </Button>
              </>
            ) : (
              status !== IPatientAppointmentStatus.CANCELLED && (
                <>
                  <Grid item className={style.cancelPhoneContainer}>
                    <Button
                      variant={IButtonVariant.WHITE}
                      btnTrigger={openCloseAppointmentCancelConfirmation}
                    >
                      <FormattedMessage id="cancel" defaultMessage="Cancel" />
                    </Button>
                    {appointmentDetail?.type_of_consultation ===
                      IDoctorScheduleConsultationType.VIDEO_CALL && (
                      <Button
                        variant={IButtonVariant.WHITE}
                        btnTrigger={() =>
                          (window.location.href = `tel:${appointmentDetail?.patient?.phoneNumber}`)
                        }
                      >
                        <GetImages
                          name="phone20by20red"
                          width="25"
                          height="20"
                        />
                        <FormattedMessage
                          id="call_patient"
                          defaultMessage="Call Patient"
                        />
                      </Button>
                    )}
                  </Grid>
                  {doctorOrJrDoctorRole &&
                    !appointmentDetail?.activePrescription &&
                    createPrescriptionBtn()}
                  {platform === IDoctorAvailabilityPlatform.IN_CLINIC ? (
                    <>
                      <Button
                        btnTrigger={() =>
                          (window.location.href = `tel:${appointmentDetail?.patient?.phoneNumber}`)
                        }
                      >
                        <GetImages
                          name="CallWhiteIcon"
                          width="25"
                          height="20"
                        />{" "}
                        Call Patient
                      </Button>
                    </>
                  ) : (
                    <Button
                      isLoading={initiateVideoCallLoading}
                      btnTrigger={initiateVideoCallHandler}
                    >
                      <GetImages name="VideoWhiteIcon" width="25" height="20" />
                      Video Call
                    </Button>
                  )}
                </>
              )
            )}
          </Grid>
        </Grid>
        {showCancelSchedule && (
          <Confirmation
            isOpen={showCancelSchedule}
            closeHandler={openCloseAppointmentCancelConfirmation}
            title="cancel_appointments"
            alterMessage={"confirm_cancel_appoinment"}
            actionHandler={() => {
              cancelScheduleAppointments();
              openCloseAppointmentCancelConfirmation();
              closeHandler();
            }}
          />
        )}
      </>
    );
  }, [showCancelSchedule, initiateVideoCallLoading]);

  if (isMobileOrTablet) {
    return (
      <>
        <Modal isOpen={isOpen} isCloseIcon closeHandler={closeHandler}>
          {content}
        </Modal>
        {showCreatePrescriptionDialog && (
          <CreatePrescription
            onClose={() => dispatch(setShowCreatePrescriptionDialog(false))}
          />
        )}
        {showMedicalRecords && <MedicalRecords onBack={handleShow} />}
      </>
    );
  }

  return (
    <>
      <Dialog maxWidth="sm" fullWidth closeHandler={closeHandler}>
        {content}
      </Dialog>
      {showCreatePrescriptionDialog && (
        <Dialog
          maxWidth="sm"
          fullWidth
          closeHandler={() => dispatch(setShowCreatePrescriptionDialog(false))}
        >
          <CreatePrescription
            onClose={() => dispatch(setShowCreatePrescriptionDialog(false))}
          />
        </Dialog>
      )}
      {showMedicalRecords && (
        <Dialog maxWidth="sm" fullWidth closeHandler={handleShow}>
          <DialogContent>
            <MedicalRecords onBack={handleShow} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ScheduleEventPopUps;
